import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PlusIcon, PencilSimpleIcon, TrashIcon, MagnifyingGlassIcon, SparkleIcon, CalendarBlankIcon, PrinterIcon } from "@phosphor-icons/react";
import { usePermission } from "@/lib/permissions";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const TYPES = ["Spa", "Gym", "Salon", "Sightseeing", "Laundry", "Adventure", "Other"];
const TYPE_TONE = {
  Spa: "bg-[hsl(190_50%_92%)] text-[hsl(190_50%_28%)] border-[hsl(190_50%_60%)]",
  Gym: "bg-[hsl(20_70%_92%)] text-[hsl(20_60%_35%)]",
  Salon: "bg-[hsl(320_60%_92%)] text-[hsl(320_60%_35%)]",
  Sightseeing: "bg-primary/10 text-primary",
  Laundry: "bg-muted text-muted-foreground",
  Adventure: "bg-[hsl(120_50%_92%)] text-[hsl(120_50%_30%)]",
  Other: "bg-secondary/15 text-secondary",
};
const EMPTY_AM = { name: "", type: "Spa", duration_minutes: 60, price: 0, gst_rate: 18, description: "", is_active: true };
const EMPTY_BK = { customer_name: "", customer_phone: "", slot_at: "", price: 0, gst_amount: 0, payment_mode: "cash", notes: "" };

export default function Amenities() {
  const { activeId, active } = useProperty();
  const { canWrite } = usePermission("amenities");
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [q, setQ] = useState("");

  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState(EMPTY_AM);
  const [editing, setEditing] = useState(null);
  const [editBooking, setEditBooking] = useState(null);

  const [bookOpen, setBookOpen] = useState(false);
  const [bookForm, setBookForm] = useState(EMPTY_BK);
  const [selectedAmId, setSelectedAmId] = useState("");

  const load = () => {
    if (!activeId) return;
    api.get("/amenities", { params: { property_id: activeId } }).then((r) => setItems(r.data));
    api.get("/amenities/bookings", { params: { property_id: activeId } }).then((r) => setBookings(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const saveItem = async () => {
    if (!itemForm.name) return toast.error("Name required");
    try {
      if (editing) await api.patch(`/amenities/${editing.id}`, itemForm);
      else await api.post("/amenities", { ...itemForm, property_id: activeId });
      toast.success(editing ? "Updated" : "Added");
      setItemOpen(false); setEditing(null); setItemForm(EMPTY_AM);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}?`)) return;
    await api.delete(`/amenities/${a.id}`); toast.success("Deleted"); load();
  };

  const saveBooking = async () => {
    if (!selectedAmId || !bookForm.customer_name || !bookForm.slot_at) return toast.error("Amenity, customer, slot required");
    const am = items.find((x) => x.id === selectedAmId);
    try {
      await api.post("/amenities/bookings", {
        ...bookForm,
        amenity_id: selectedAmId,
        property_id: activeId,
        price: Number(bookForm.price) || (am?.price || 0),
        gst_amount: Number(bookForm.gst_amount) || ((am?.price || 0) * (am?.gst_rate || 0) / 100),
      });
      toast.success("Booking created");
      setBookOpen(false); setBookForm(EMPTY_BK); setSelectedAmId("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const complete = async (b) => {
    await api.patch(`/amenities/bookings/${b.id}`, { status: "completed" });
    toast.success("Marked complete"); load();
  };

  const filteredBk = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return bookings;
    return bookings.filter((b) =>
      (b.booking_ref || "").toLowerCase().includes(term) ||
      (b.customer_name || "").toLowerCase().includes(term) ||
      (items.find((a) => a.id === b.amenity_id)?.name || "").toLowerCase().includes(term)
    );
  }, [bookings, items, q]);

  return (
    <div>
      <PageHeader
        overline={active ? active.name : "Property"}
        title="Amenities & Services"
        description="Spa, gym, salon, sightseeing — configure services, take slot bookings, and post charges to a room folio."
      />
      <Tabs value={tab} onValueChange={setTab} className="mt-4">
        <TabsList className="mb-6">
          <TabsTrigger value="bookings" data-testid="amenities-tab-bookings"><CalendarBlankIcon size={14} className="mr-1" />Bookings</TabsTrigger>
          <TabsTrigger value="services" data-testid="amenities-tab-services"><SparkleIcon size={14} className="mr-1" />Services</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          <Card className="p-4 mb-4 flex justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px] relative">
              <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search booking / guest / service…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="amenities-search" />
            </div>
            {canWrite && (
              <Dialog open={bookOpen} onOpenChange={setBookOpen}>
                <DialogTrigger asChild><Button data-testid="amenities-new-booking-btn"><PlusIcon size={14} className="mr-1" />Book slot</Button></DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Book amenity slot</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Service</Label>
                      <Select value={selectedAmId} onValueChange={setSelectedAmId}>
                        <SelectTrigger data-testid="amenities-book-service"><SelectValue placeholder="Pick a service" /></SelectTrigger>
                        <SelectContent>
                          {items.filter((a) => a.is_active).map((a) => (
                            <SelectItem key={a.id} value={a.id}>{a.name} · {a.type} · {a.duration_minutes}m · {inr(a.price)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Customer *</Label><Input value={bookForm.customer_name} onChange={(e) => setBookForm({ ...bookForm, customer_name: e.target.value })} data-testid="amenities-book-customer" /></div>
                    <div><Label>Phone</Label><Input value={bookForm.customer_phone} onChange={(e) => setBookForm({ ...bookForm, customer_phone: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Slot start *</Label><Input type="datetime-local" value={bookForm.slot_at} onChange={(e) => setBookForm({ ...bookForm, slot_at: e.target.value })} data-testid="amenities-book-slot" /></div>
                    <div><Label>Payment mode</Label>
                      <Select value={bookForm.payment_mode} onValueChange={(v) => setBookForm({ ...bookForm, payment_mode: v })}>
                        <SelectTrigger data-testid="amenities-book-payment"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                          <SelectItem value="room_charge">Post to room</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Notes</Label><Input value={bookForm.notes} onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveBooking} data-testid="amenities-book-save">Book</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </Card>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Slot</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBk.map((b) => {
                  const am = items.find((a) => a.id === b.amenity_id);
                  return (
                    <TableRow key={b.id} data-testid={`amenities-booking-row-${b.id}`}>
                      <TableCell className="font-mono-plex text-xs">{b.booking_ref}</TableCell>
                      <TableCell>{am?.name || "—"} <Badge variant="outline" className={TYPE_TONE[am?.type] || ""}>{am?.type}</Badge></TableCell>
                      <TableCell>{b.customer_name}<div className="text-[10px] text-muted-foreground">{b.customer_phone}</div></TableCell>
                      <TableCell className="text-xs">{new Date(b.slot_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                      <TableCell className="text-right font-mono-plex">{inr(b.price)}</TableCell>
                      <TableCell className="text-xs uppercase">{b.payment_mode?.replace("_", " ")}</TableCell>
                      <TableCell><Badge variant="outline">{b.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => window.open(`/amenities/bookings/${b.id}/print`, "_blank")} data-testid={`amenities-print-${b.id}`}><PrinterIcon size={12} className="mr-1" />Print</Button>
                        {canWrite && b.status === "booked" && <Button size="sm" variant="outline" onClick={() => setEditBooking(b)} data-testid={`amenities-edit-booking-${b.id}`} className="ml-1"><PencilSimpleIcon size={12} className="mr-1" />Edit</Button>}
                        {canWrite && b.status === "booked" && <Button size="sm" variant="outline" onClick={() => complete(b)} data-testid={`amenities-complete-${b.id}`} className="ml-1">Mark done</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredBk.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground italic">No bookings.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="services">
          <Card className="p-4 mb-4 flex justify-between">
            <div>
              <p className="label-overline">Services</p>
              <p className="text-sm text-muted-foreground">Add spa treatments, gym passes, salon services, sightseeing packages.</p>
            </div>
            {canWrite && (
              <Dialog open={itemOpen} onOpenChange={(v) => { setItemOpen(v); if (!v) { setEditing(null); setItemForm(EMPTY_AM); } }}>
                <DialogTrigger asChild><Button data-testid="amenities-add-btn"><PlusIcon size={14} className="mr-1" />Add service</Button></DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Name *</Label><Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} data-testid="amenities-form-name" /></div>
                    <div>
                      <Label>Type</Label>
                      <Select value={itemForm.type} onValueChange={(v) => setItemForm({ ...itemForm, type: v })}>
                        <SelectTrigger data-testid="amenities-form-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Duration (min)</Label><Input type="number" value={itemForm.duration_minutes} onChange={(e) => setItemForm({ ...itemForm, duration_minutes: Number(e.target.value) })} data-testid="amenities-form-duration" /></div>
                    <div><Label>Price (₹)</Label><Input type="number" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: Number(e.target.value) })} data-testid="amenities-form-price" /></div>
                    <div><Label>GST rate (%)</Label><Input type="number" value={itemForm.gst_rate} onChange={(e) => setItemForm({ ...itemForm, gst_rate: Number(e.target.value) })} /></div>
                    <div className="col-span-2"><Label>Description</Label><Input value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveItem} data-testid="amenities-form-save">{editing ? "Save" : "Add"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </Card>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead>GST</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} data-testid={`amenities-row-${a.id}`}>
                    <TableCell><strong>{a.name}</strong><div className="text-[10px] text-muted-foreground">{a.description}</div></TableCell>
                    <TableCell><Badge variant="outline" className={TYPE_TONE[a.type] || ""}>{a.type}</Badge></TableCell>
                    <TableCell>{a.duration_minutes} min</TableCell>
                    <TableCell className="text-right font-mono-plex">{inr(a.price)}</TableCell>
                    <TableCell className="text-xs">{a.gst_rate}%</TableCell>
                    <TableCell><Badge variant="outline" className={a.is_active ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-muted"}>{a.is_active ? "Active" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => { setEditing(a); setItemForm(a); setItemOpen(true); }}><PencilSimpleIcon size={12} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(a)} className="text-destructive"><TrashIcon size={12} /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground italic">No services configured.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {editBooking && (
        <Dialog open={!!editBooking} onOpenChange={(v) => !v && setEditBooking(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="amenities-edit-booking-dialog">
            <DialogHeader><DialogTitle>Edit booking · {editBooking.booking_ref}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Slot at</Label><Input type="datetime-local" value={(editBooking.slot_at || "").slice(0, 16)} onChange={(e) => setEditBooking({ ...editBooking, slot_at: e.target.value })} data-testid="amenities-edit-slot" /></div>
              <div>
                <Label>Payment mode</Label>
                <Select value={editBooking.payment_mode || "cash"} onValueChange={(v) => setEditBooking({ ...editBooking, payment_mode: v })}>
                  <SelectTrigger data-testid="amenities-edit-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["cash", "upi", "card", "room_charge", "travels", "bank", "other"].map((p) => <SelectItem key={p} value={p}>{p.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Price (₹)</Label><Input type="number" value={editBooking.price || 0} onChange={(e) => setEditBooking({ ...editBooking, price: Number(e.target.value) })} data-testid="amenities-edit-price" /></div>
              <div><Label>GST amount (₹)</Label><Input type="number" value={editBooking.gst_amount || 0} onChange={(e) => setEditBooking({ ...editBooking, gst_amount: Number(e.target.value) })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={editBooking.notes || ""} onChange={(e) => setEditBooking({ ...editBooking, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditBooking(null)}>Cancel</Button>
              <Button onClick={async () => {
                try {
                  const { id, ...updates } = editBooking;
                  await api.patch(`/amenities/bookings/${id}`, updates);
                  toast.success("Booking updated");
                  setEditBooking(null);
                  loadBookings();
                } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
              }} data-testid="amenities-edit-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
  function loadBookings() {
    api.get("/amenities/bookings", { params: { property_id: activeId } }).then((r) => setBookings(r.data)).catch(() => {});
  }
}
